import React, { useState, useEffect } from 'react';
import { GitCompare, Plus, X, ArrowUpRight, ArrowDownRight, Minus, BarChart3, Download, TrendingUp } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { getMetricColor, getPerformanceLabel } from '@/utils/metricThresholds';
import StatisticalAnalysis from '@/components/comparison/StatisticalAnalysis';

const fc = (v) => typeof v === 'number' ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0';
const fp = (v) => typeof v === 'number' ? `${v.toFixed(2)}%` : '0%';

// Helper to format scenario name as "Municipality - State - Service"
const formatScenarioName = (scenario) => {
  if (!scenario) return 'Unknown';
  const parts = [];
  
  // Backend returns municipality/state at top level, extracted from scenario_info
  const municipality = (scenario.municipality || scenario.scenario_info?.municipality || '').trim();
  const state = (scenario.state || scenario.scenario_info?.state || '').trim();
  const service = (scenario.service_name || scenario.scenario_info?.service || '').trim();
  
  if (municipality) parts.push(municipality);
  if (state) parts.push(state);
  if (service) parts.push(service);
  
  return parts.length > 0 ? parts.join(' - ') : (scenario.name || 'Scenario');
};

const generateComparisonCSV = (results) => {
  if (!results || !results.scenarios) return '';
  
  // Calculate totals for percentage contributions
  const totalGrossRevenue = results.scenarios.reduce((sum, s) => sum + (s.gross_revenue || 0), 0);
  const totalNetRevenue = results.scenarios.reduce((sum, s) => sum + (s.net_revenue || 0), 0);
  
  const headers = ['Metric', ...results.scenarios.map(s => s.name)];
  const rows = [
    ['Service', ...results.scenarios.map(s => s.service_name)],
    ['Municipality', ...results.scenarios.map(s => `${s.municipality}, ${s.state}`)],
    ['Annual Transactions', ...results.scenarios.map(s => s.annual_transactions || 0)],
    ['Total Payment Volume', ...results.scenarios.map(s => s.total_payments_volume || 0)],
    ['Gross Revenue', ...results.scenarios.map(s => s.gross_revenue || 0)],
    ['% Contribution (Gross)', ...results.scenarios.map(s => {
      const percentage = totalGrossRevenue > 0 ? ((s.gross_revenue || 0) / totalGrossRevenue * 100) : 0;
      return `${percentage.toFixed(1)}%`;
    })],
    ['Δ Gross Revenue vs Base', ...results.scenarios.map((s, i) => i === 0 ? 0 : s.delta_gross_revenue || 0)],
    ['Merchant Fees', ...results.scenarios.map(s => s.merchant_fees || 0)],
    ['Net Revenue', ...results.scenarios.map(s => s.net_revenue || 0)],
    ['% Contribution (Net)', ...results.scenarios.map(s => {
      const percentage = totalNetRevenue > 0 ? ((s.net_revenue || 0) / totalNetRevenue * 100) : 0;
      return `${percentage.toFixed(1)}%`;
    })],
    ['Δ Net Revenue vs Base', ...results.scenarios.map((s, i) => i === 0 ? 0 : s.delta_net_revenue || 0)],
    ['GPM %', ...results.scenarios.map(s => s.gpm || 0)],
    ['Δ GPM vs Base', ...results.scenarios.map((s, i) => i === 0 ? 0 : s.delta_gpm || 0)],
    ['Net Margin %', ...results.scenarios.map(s => s.net_margin || 0)],
    ['Billing Frequency', ...results.scenarios.map(s => s.billing_frequency || 'N/A')],
  ];
  
  const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
  return csv;
};

export default function ComparisonView() {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/analytics/comparison-candidates');
        setCandidates(res.data.candidates || []);
      } catch {
        toast.error('Failed to load scenarios');
      }
    };
    load();
  }, []);

  const addToComparison = (id) => {
    if (selected.includes(id)) return;
    if (selected.length >= 5) {
      toast.error('Maximum 5 scenarios for comparison');
      return;
    }
    setSelected([...selected, id]);
  };

  const removeFromComparison = (id) => {
    setSelected(selected.filter(s => s !== id));
    if (results) setResults(null);
  };

  const runComparison = async () => {
    if (selected.length < 2) {
      toast.error('Select at least 2 scenarios to compare');
      return;
    }
    try {
      setLoading(true);
      const res = await api.post('/analytics/compare', { scenario_ids: selected });
      setResults(res.data);
    } catch (err) {
      toast.error('Comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const filtered = candidates.filter(c =>
    !selected.includes(c.id) &&
    (c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     c.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     c.municipality?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const DeltaCell = ({ value, format = 'currency' }) => {
    if (value === undefined || value === null) return <td className="px-4 py-3 text-center text-slate-300">—</td>;
    const isPos = value > 0;
    const isZero = Math.abs(value) < 0.01;
    const display = format === 'currency' ? fc(Math.abs(value)) : fp(Math.abs(value));
    return (
      <td className={`px-4 py-3 text-center text-sm font-medium ${isZero ? 'text-slate-400' : isPos ? 'text-emerald-600' : 'text-red-500'}`}>
        <span className="inline-flex items-center gap-1">
          {isZero ? <Minus className="w-3 h-3" /> : isPos ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {isZero ? '—' : `${isPos ? '+' : '-'}${display}`}
        </span>
      </td>
    );
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] w-full mx-auto space-y-8 animate-fade-in" data-testid="comparison-page">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2.5">
          <GitCompare className="w-5 h-5 text-sky-600" />
          Scenario Comparison
        </h1>
        <p className="text-sm text-slate-400 mt-0.5">Compare pricing scenarios side-by-side</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scenario Picker */}
        <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-800">Available Scenarios</h3>
            <input
              data-testid="comparison-search"
              type="text"
              placeholder="Search by name, service, municipality..."
              className="mt-2 w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 outline-none focus:border-slate-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
            {filtered.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-400 text-center">No matching scenarios</p>
            ) : (
              filtered.map(c => (
                <div
                  key={c.id}
                  className="flex items-center justify-between px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                  onClick={() => addToComparison(c.id)}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{c.name}</p>
                    <p className="text-xs text-slate-400">
                      {c.service_name || 'No Service'} 
                      {(c.municipality || c.scenario_info?.municipality) && 
                       ` · ${c.municipality || c.scenario_info?.municipality}, ${c.state || c.scenario_info?.state || '?'}`
                      }
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-slate-400">{fc(c.gross_revenue)}</span>
                    <Plus className="w-4 h-4 text-slate-300 hover:text-sky-500" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Selected + Results */}
        <div className="lg:col-span-2 space-y-4">
          {/* Selected chips */}
          <div className="bg-white rounded-xl border border-slate-200/80 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-800">Selected ({selected.length}/5)</h3>
              <button
                data-testid="run-comparison-btn"
                disabled={selected.length < 2 || loading}
                onClick={runComparison}
                className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-40 transition-colors"
              >
                {loading ? 'Comparing...' : 'Compare'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selected.length === 0 && (
                <p className="text-sm text-slate-400">Click scenarios from the list to add them here</p>
              )}
              {selected.map(id => {
                const c = candidates.find(x => x.id === id);
                if (!c) return null;
                
                // Format as: "Municipality - Service" or use scenario_info if available
                const displayName = c.scenario_info?.municipality && c.service_name
                  ? `${c.scenario_info.municipality} - ${c.service_name}`
                  : c.municipality && c.service_name
                  ? `${c.municipality} - ${c.service_name}`
                  : c.name || id.slice(0, 8);
                
                return (
                  <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 text-sky-700 rounded-lg text-xs font-medium">
                    {displayName}
                    <button onClick={() => removeFromComparison(id)}>
                      <X className="w-3.5 h-3.5 hover:text-red-500" />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Intelligent Insights */}
          {results && results.scenarios && results.scenarios.length > 0 && (
            <>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 p-5">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-blue-900 mb-2">🎯 Intelligent Analysis</h3>
                  
                  {(() => {
                    try {
                      const scenarios = results.scenarios;
                      
                      // Ensure all scenarios have required numeric fields
                      const validScenarios = scenarios.filter(s => 
                        s && 
                        typeof s.gross_revenue === 'number' && 
                        typeof s.net_revenue === 'number' && 
                        typeof s.net_margin === 'number' && 
                        typeof s.gpm === 'number'
                      );
                      
                      if (validScenarios.length === 0) {
                        return <p className="text-sm text-blue-700">Insufficient data for analysis</p>;
                      }
                      
                      const bestGrossRevenue = validScenarios.reduce((max, s) => 
                        (s.gross_revenue || 0) > (max.gross_revenue || 0) ? s : max
                      );
                      const bestNetRevenue = validScenarios.reduce((max, s) => 
                        (s.net_revenue || 0) > (max.net_revenue || 0) ? s : max
                      );
                      const bestMargin = validScenarios.reduce((max, s) => 
                        (s.net_margin || 0) > (max.net_margin || 0) ? s : max
                      );
                      const bestGPM = validScenarios.reduce((max, s) => 
                        (s.gpm || 0) > (max.gpm || 0) ? s : max
                      );
                      
                      // Calculate efficiency score for each scenario
                      const withScores = validScenarios.map(s => ({
                        ...s,
                        efficiency_score: ((s.net_revenue || 0) / (s.gross_revenue || 1) * 100) + 
                                         ((s.gpm || 0) / 2) + 
                                         ((s.net_margin || 0) * 2)
                      }));
                      const bestOverall = withScores.reduce((max, s) => 
                        (s.efficiency_score || 0) > (max.efficiency_score || 0) ? s : max
                      );
                      
                      return (
                        <div className="space-y-2 text-sm text-blue-800">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            <p><span className="font-semibold">{formatScenarioName(bestOverall)}</span> is the <span className="font-bold text-green-700">BEST OVERALL</span> choice (highest efficiency score)</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            <p><span className="font-semibold">{formatScenarioName(bestGrossRevenue)}</span> generates highest gross revenue ({fc(bestGrossRevenue.gross_revenue)})</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                            <p><span className="font-semibold">{formatScenarioName(bestNetRevenue)}</span> has highest net revenue ({fc(bestNetRevenue.net_revenue)})</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                            <p>
                              <span className="font-semibold">{formatScenarioName(bestMargin)}</span> has best net margin (
                              <span className={`font-bold ${getMetricColor(bestMargin.net_margin, 'netMargin').textColor}`}>
                                {fp(bestMargin.net_margin)} {getMetricColor(bestMargin.net_margin, 'netMargin').indicator}
                              </span>
                              )
                            </p>
                          </div>
                          
                          {bestOverall.scenario_id === bestNetRevenue.scenario_id && bestOverall.scenario_id === bestMargin.scenario_id && (
                            <div className="mt-3 p-3 bg-green-100 border-2 border-green-400 rounded-lg">
                              <p className="font-bold text-green-900">💚 Unanimous Winner: {formatScenarioName(bestOverall)}</p>
                              <p className="text-xs text-green-700 mt-1">Leads in revenue, margin, and overall efficiency</p>
                            </div>
                          )}
                          
                          {validScenarios.length >= 3 && (() => {
                            const worstMargin = validScenarios.reduce((min, s) => 
                              (s.net_margin || 0) < (min.net_margin || 0) ? s : min
                            );
                            if ((bestMargin.net_margin || 0) - (worstMargin.net_margin || 0) > 5) {
                              return (
                                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                                  <p className="text-xs text-amber-800">⚠️ <span className="font-semibold">{formatScenarioName(worstMargin)}</span> has significantly lower margin ({fp(worstMargin.net_margin)} vs {fp(bestMargin.net_margin)}) - consider optimization</p>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      );
                    } catch (error) {
                      console.error('Error generating insights:', error);
                      return <p className="text-sm text-red-600">Unable to generate insights. Please try again.</p>;
                    }
                  })()}
                </div>
              </div>
            </div>

            {/* Statistical Analysis Section */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                  <h3 className="text-lg font-bold text-slate-900">Advanced Statistical Analysis</h3>
                </div>
                <span className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                  {results.scenarios.length} scenarios analyzed
                </span>
              </div>
              <StatisticalAnalysis scenarios={results.scenarios} />
            </div>
            </>
          )}

          {/* Results Table */}
          {results && (
            <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Comparison Results</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Deltas shown relative to the first scenario (baseline)</p>
                </div>
                <button
                  onClick={() => {
                    const csv = generateComparisonCSV(results);
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `scenario_comparison_${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success('Comparison exported to CSV');
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Metric</th>
                      {results.scenarios.map((s, i) => (
                        <th key={s.scenario_id} className="text-center px-4 py-2.5 text-xs font-semibold text-slate-700">
                          <div className="flex flex-col">
                            <span className="font-bold">{formatScenarioName(s)}</span>
                            {i === 0 && <span className="text-[10px] text-sky-500 font-normal mt-0.5">(Baseline)</span>}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    <tr>
                      <td className="px-4 py-3 text-slate-600 font-medium">Service</td>
                      {results.scenarios.map(s => <td key={s.scenario_id} className="px-4 py-3 text-center text-slate-700">{s.service_name}</td>)}
                    </tr>
                    <tr className="bg-slate-50/50">
                      <td className="px-4 py-3 text-slate-600 font-medium">Municipality</td>
                      {results.scenarios.map(s => <td key={s.scenario_id} className="px-4 py-3 text-center text-slate-700">{s.municipality}, {s.state}</td>)}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-slate-600 font-medium">Annual Txns</td>
                      {results.scenarios.map(s => <td key={s.scenario_id} className="px-4 py-3 text-center text-slate-700">{s.annual_transactions?.toLocaleString()}</td>)}
                    </tr>
                    <tr className="bg-slate-50/50">
                      <td className="px-4 py-3 text-slate-600 font-medium">Total Payment Volume</td>
                      {results.scenarios.map(s => <td key={s.scenario_id} className="px-4 py-3 text-center text-slate-700 font-medium">{fc(s.total_payments_volume)}</td>)}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-slate-600 font-medium">Gross Revenue</td>
                      {results.scenarios.map(s => <td key={s.scenario_id} className="px-4 py-3 text-center text-slate-700 font-semibold">{fc(s.gross_revenue)}</td>)}
                    </tr>
                    <tr className="bg-blue-50/40">
                      <td className="px-4 py-3 text-slate-600 font-medium text-xs">  % Contribution (Gross)</td>
                      {(() => {
                        const totalGrossRevenue = results.scenarios.reduce((sum, s) => sum + (s.gross_revenue || 0), 0);
                        return results.scenarios.map(s => {
                          const percentage = totalGrossRevenue > 0 ? ((s.gross_revenue || 0) / totalGrossRevenue * 100) : 0;
                          return (
                            <td key={s.scenario_id} className="px-4 py-3 text-center text-blue-700 font-medium text-sm">
                              {percentage.toFixed(1)}%
                            </td>
                          );
                        });
                      })()}
                    </tr>
                    <tr className="bg-emerald-50/30">
                      <td className="px-4 py-3 text-slate-600 font-medium">  &Delta; vs Base</td>
                      {results.scenarios.map((s, i) => <DeltaCell key={s.scenario_id} value={i === 0 ? null : s.delta_gross_revenue} />)}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-slate-600 font-medium">Merchant Fees</td>
                      {results.scenarios.map(s => <td key={s.scenario_id} className="px-4 py-3 text-center text-slate-700">{fc(s.merchant_fees)}</td>)}
                    </tr>
                    <tr className="bg-slate-50/50">
                      <td className="px-4 py-3 text-slate-600 font-medium">Net Revenue</td>
                      {results.scenarios.map(s => <td key={s.scenario_id} className="px-4 py-3 text-center text-slate-700 font-semibold">{fc(s.net_revenue)}</td>)}
                    </tr>
                    <tr className="bg-purple-50/40">
                      <td className="px-4 py-3 text-slate-600 font-medium text-xs">  % Contribution (Net)</td>
                      {(() => {
                        const totalNetRevenue = results.scenarios.reduce((sum, s) => sum + (s.net_revenue || 0), 0);
                        return results.scenarios.map(s => {
                          const percentage = totalNetRevenue > 0 ? ((s.net_revenue || 0) / totalNetRevenue * 100) : 0;
                          return (
                            <td key={s.scenario_id} className="px-4 py-3 text-center text-purple-700 font-medium text-sm">
                              {percentage.toFixed(1)}%
                            </td>
                          );
                        });
                      })()}
                    </tr>
                    <tr className="bg-emerald-50/30">
                      <td className="px-4 py-3 text-slate-600 font-medium">  &Delta; vs Base</td>
                      {results.scenarios.map((s, i) => <DeltaCell key={s.scenario_id} value={i === 0 ? null : s.delta_net_revenue} />)}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-slate-600 font-medium">GPM</td>
                      {results.scenarios.map(s => {
                        const colors = getMetricColor(s.gpm, 'gpm');
                        return (
                          <td key={s.scenario_id} className={`px-4 py-3 text-center font-semibold ${colors.textColor}`}>
                            <div className="flex items-center justify-center gap-1">
                              <span>{colors.indicator}</span>
                              <span>{fp(s.gpm)}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    <tr className="bg-emerald-50/30">
                      <td className="px-4 py-3 text-slate-600 font-medium">  &Delta; vs Base</td>
                      {results.scenarios.map((s, i) => <DeltaCell key={s.scenario_id} value={i === 0 ? null : s.delta_gpm} format="percent" />)}
                    </tr>
                    <tr className="bg-slate-50/50">
                      <td className="px-4 py-3 text-slate-600 font-medium">Net Margin</td>
                      {results.scenarios.map(s => {
                        const colors = getMetricColor(s.net_margin, 'netMargin');
                        const label = getPerformanceLabel(s.net_margin, 'netMargin');
                        return (
                          <td key={s.scenario_id} className={`px-4 py-3 text-center font-semibold ${colors.textColor}`}>
                            <div className="flex flex-col items-center">
                              <div className="flex items-center gap-1">
                                <span>{colors.indicator}</span>
                                <span>{fp(s.net_margin)}</span>
                              </div>
                              <span className="text-[10px] font-normal mt-0.5">{label}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-slate-600 font-medium">Billing Frequency</td>
                      {results.scenarios.map(s => <td key={s.scenario_id} className="px-4 py-3 text-center text-slate-700">{s.billing_frequency}</td>)}
                    </tr>
                    <tr className="bg-slate-50/50">
                      <td className="px-4 py-3 text-slate-600 font-medium">Payment Mix</td>
                      {results.scenarios.map(s => (
                        <td key={s.scenario_id} className="px-4 py-3 text-center text-xs text-slate-500">
                          CC {s.payment_mix?.cc}% &middot; AMEX {s.payment_mix?.amex}% &middot; Debit {s.payment_mix?.debit}% &middot; ACH {s.payment_mix?.ach}%
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
